import React, { useState, useRef, useEffect } from 'react';
import { Search, MapPin, ChevronDown, X, Check, LayoutGrid, Banknote, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useProvinces, useDistricts } from '@/hooks/useAddress';
import { useAuthStore } from '@/store/authStore';
import { Link } from 'react-router-dom';
import { NumberInput } from '@/components/ui/numberInput';

const RENT_RANGES = [
  { label: 'Tất cả', min: null, max: null },
  { label: '< 20 triệu', min: 0, max: 20000000 },
  { label: '20 - 40 triệu', min: 20000000, max: 40000000 },
  { label: '40 - 60 triệu', min: 40000000, max: 60000000 },
  { label: '60 - 100 triệu', min: 60000000, max: 100000000 },
  { label: '> 100 triệu', min: 100000000, max: null },
];

const PROPERTY_TYPES = [
  { value: 'commercial', label: 'Mặt bằng thương mại' },
  { value: 'shophouse', label: 'Shophouse' },
  { value: 'street_house', label: 'Nhà mặt phố' },
  { value: 'building', label: 'Tòa nhà' },
  { value: 'land', label: 'Đất trống' },
];

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Nhà hàng' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'bar', label: 'Bar / Lounge' },
  { value: 'bakery', label: 'Tiệm bánh' },
  { value: 'food court stall', label: 'Food Court' },
];

interface RentalFilterProps {
  type?: 'property' | 'request';
  onSearch: (filters: any) => void;
  initialFilters?: any;
}

export const RentalFilter: React.FC<RentalFilterProps> = ({ type = 'property', onSearch, initialFilters = {} }) => {
  const { isAuthenticated } = useAuthStore();
  const [search, setSearch] = useState(initialFilters.search || '');

  // Type State
  const [businessType, setBusinessType] = useState<string | null>(initialFilters.businessType || initialFilters.propertyType || null);
  const [tempBusinessType, setTempBusinessType] = useState<string | null>(initialFilters.businessType || initialFilters.propertyType || null);

  // Location State
  const [tempProvinceId, setTempProvinceId] = useState<number | null>(initialFilters.provinceId || null);
  const [tempDistrictId, setTempDistrictId] = useState<number | null>(initialFilters.districtId || null);
  const [provinceId, setProvinceId] = useState<number | null>(initialFilters.provinceId || null);
  const [districtId, setDistrictId] = useState<number | null>(initialFilters.districtId || null);

  // Rent range State
  const [priceMin, setPriceMin] = useState<number | null>(initialFilters.monthlyRentMin || initialFilters.minBudget || null);
  const [priceMax, setPriceMax] = useState<number | null>(initialFilters.monthlyRentMax || initialFilters.maxBudget || null);

  // Advanced Filters State
  const [propertyType, setPropertyType] = useState<string | null>(initialFilters.propertyType || null);
  const [seatingCapacityMin, setSeatingCapacityMin] = useState<number | null>(initialFilters.seatingCapacityMin || null);
  const [seatingCapacityMax, setSeatingCapacityMax] = useState<number | null>(initialFilters.seatingCapacityMax || null);
  const [minArea, setMinArea] = useState<number | null>(initialFilters.minArea || null);
  const [maxArea, setMaxArea] = useState<number | null>(initialFilters.maxArea || null);
  const [hasParking, setHasParking] = useState<boolean>(initialFilters.hasParking || false);
  const [hasOutdoorSeating, setHasOutdoorSeating] = useState<boolean>(initialFilters.hasOutdoorSeating || false);
  const [isAvailable, setIsAvailable] = useState<boolean>(initialFilters.isAvailable || false);

  const [activeDropdown, setActiveDropdown] = useState<'type' | 'location' | 'rent' | 'advanced' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Data Hooks
  const { data: provinces = [] } = useProvinces(1); // Vietnam
  const { data: districts = [] } = useDistricts(tempProvinceId || undefined);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeDropdown === 'location') {
      setTempProvinceId(provinceId);
      setTempDistrictId(districtId);
    }
  }, [activeDropdown, provinceId, districtId]);

  useEffect(() => {
    if (activeDropdown === 'type') {
      setTempBusinessType(businessType);
    }
  }, [activeDropdown, businessType]);

  const getFiltersToEmit = (overrides: any = {}) => {
    const pId = overrides.provinceId !== undefined ? overrides.provinceId : provinceId;
    const dId = overrides.districtId !== undefined ? overrides.districtId : districtId;
    const bType = overrides.businessType !== undefined ? overrides.businessType : businessType;

    const provName = provinces.find((p: any) => p.id === pId)?.name || '';
    const distName = districts.find((d: any) => d.id === dId)?.name || '';

    const payload: any = {
      page: initialFilters.page || 1,
      limit: initialFilters.limit || 9,
      language: initialFilters.language || 'vi',
      orderBy: initialFilters.orderBy || 'createdAt',
      orderDirection: initialFilters.orderDirection || 'DESC',
    };

    if (type === 'property') {
      if (bType) payload.businessType = bType;
      if (propertyType) payload.propertyType = propertyType;
      if (priceMin !== null) payload.monthlyRentMin = priceMin;
      if (priceMax !== null) payload.monthlyRentMax = priceMax;
      if (seatingCapacityMin !== null) payload.seatingCapacityMin = seatingCapacityMin;
      if (seatingCapacityMax !== null) payload.seatingCapacityMax = seatingCapacityMax;
      if (hasParking) payload.hasParking = true;
      if (hasOutdoorSeating) payload.hasOutdoorSeating = true;
      if (isAvailable) payload.isAvailable = true;
      if (provName) payload.province = provName;
      if (distName) payload.district = distName;
    } else {
      if (bType) payload.businessType = bType;
      if (propertyType) payload.propertyType = propertyType;
      if (priceMin !== null) payload.budgetMin = priceMin;
      if (priceMax !== null) payload.budgetMax = priceMax;
      if (provName) payload.province = provName;
      if (distName) payload.district = distName;
    }
    return payload;
  };

  const handleSearch = () => {
    onSearch(getFiltersToEmit());
    setActiveDropdown(null);
  };

  const applyType = () => {
    setBusinessType(tempBusinessType);
    onSearch(getFiltersToEmit({ businessType: tempBusinessType }));
    setActiveDropdown(null);
  };

  const applyLocation = () => {
    setProvinceId(tempProvinceId);
    setDistrictId(tempDistrictId);
    onSearch(getFiltersToEmit({ provinceId: tempProvinceId, districtId: tempDistrictId }));
    setActiveDropdown(null);
  };

  const resetAll = () => {
    setSearch('');
    setBusinessType(null);
    setTempBusinessType(null);
    setProvinceId(null);
    setDistrictId(null);
    setTempProvinceId(null);
    setTempDistrictId(null);
    setPriceMin(null);
    setPriceMax(null);
    setPropertyType(null);
    setSeatingCapacityMin(null);
    setSeatingCapacityMax(null);
    setMinArea(null);
    setMaxArea(null);
    setHasParking(false);
    setHasOutdoorSeating(false);
    setIsAvailable(false);

    onSearch({
      page: 1,
      limit: initialFilters.limit || 9,
      language: initialFilters.language || 'vi',
      orderBy: initialFilters.orderBy || 'createdAt',
      orderDirection: initialFilters.orderDirection || 'DESC',
    });
    setActiveDropdown(null);
  };

  const t = {
    bgActive: 'bg-orange-50',
    textActive: 'text-orange-600',
    textIcon: 'text-orange-500',
    borderActive: 'border-orange-500',
    bgButton: 'bg-orange-500',
    bgButtonHover: 'hover:bg-orange-600',
    shadowButton: 'shadow-orange-200',
    hoverBorder: 'hover:border-orange-200',
    focusBorder: 'focus:border-orange-500',
    focusRing: 'focus:ring-orange-500/10',
    textHover: 'hover:text-orange-500',
    borderBtn: 'border-orange-500 border-4',
  };

  return (
    <div className="w-full max-w-7xl mx-auto mb-6" ref={containerRef}>
      <div className="relative flex flex-col md:flex-row items-stretch bg-white rounded-2xl shadow-xl border border-gray-100 p-2 gap-2 md:gap-0">

        {/* Type Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'type' ? null : 'type')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'type' && "bg-gray-50"
            )}
          >
            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", t.bgActive, t.textIcon)}>
              <LayoutGrid size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {businessType ? BUSINESS_TYPES.find(b => b.value === businessType)?.label || BUSINESS_TYPES.find(b => b.value === businessType?.toUpperCase())?.label || businessType : "Mô hình F&B"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {businessType && (
                <div
                  onClick={(e) => { e.stopPropagation(); setBusinessType(null); setTempBusinessType(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'type' && "rotate-180")} />
            </div>
          </button>

          {activeDropdown === 'type' && (
            <div className="absolute top-full left-0 mt-3 w-full md:w-[350px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Mô hình F&B</h3>
                <button onClick={() => setTempBusinessType(null)} className={cn("text-xs font-medium hover:underline", t.textIcon)}>Xóa chọn</button>
              </div>
              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-2">
                <div className="grid grid-cols-1 gap-1">
                  <button
                    onClick={() => setTempBusinessType(null)}
                    className={cn(
                      "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all",
                      !tempBusinessType ? `${t.bgActive} ${t.textActive} font-semibold` : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <span>Tất cả mô hình</span>
                    {!tempBusinessType && <Check size={16} />}
                  </button>
                  {BUSINESS_TYPES.map(bt => (
                    <button
                      key={bt.value}
                      onClick={() => setTempBusinessType(bt.value)}
                      className={cn(
                        "flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all",
                        tempBusinessType === bt.value ? `${t.bgActive} ${t.textActive} font-semibold` : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      <span>{bt.label}</span>
                      {tempBusinessType === bt.value && <Check size={16} />}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={applyType} className={cn("px-8 h-10 rounded-xl text-sm font-bold text-white transition-all shadow-md", t.bgButton, t.bgButtonHover, t.shadowButton)}>Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Search Input */}
        {/* <div className="flex-[1.2] flex items-center px-3 group min-w-0">
          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mr-2", t.bgActive, t.textIcon)}>
            <Search size={18} />
          </div>
          <div className="flex-1 relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={type === 'property' ? "Tìm theo tên mặt bằng, địa chỉ..." : "Tìm theo địa chỉ, loại hình..."}
              className="w-full text-sm font-semibold text-gray-700 bg-transparent outline-none placeholder:text-gray-300 pr-8"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); onSearch({ ...initialFilters, search: undefined }); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div> */}

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Location Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'location' ? null : 'location')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'location' && "bg-gray-50"
            )}
          >
            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-green-50 text-green-500")}>
              <MapPin size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {provinceId ? provinces.find((p: any) => p.id === provinceId)?.name : "Toàn quốc"}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {provinceId !== null && (
                <div
                  onClick={(e) => { e.stopPropagation(); setProvinceId(null); setDistrictId(null); setTempProvinceId(null); setTempDistrictId(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'location' && "rotate-180")} />
            </div>
          </button>

          {activeDropdown === 'location' && (
            <div className="absolute top-full right-0 mt-3 w-full md:w-[700px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <span className="font-bold text-gray-800">Khu vực</span>
                <button onClick={() => { setTempProvinceId(null); setTempDistrictId(null); }} className={cn("text-xs font-medium hover:underline", t.textIcon)}>Xóa chọn</button>
              </div>

              <div className="flex h-[300px]">
                {/* Left: Provinces */}
                <div className="w-2/5 border-r overflow-y-auto custom-scrollbar">
                  <div className="p-2 space-y-1">
                    {provinces.map((p: any) => (
                      <div
                        key={p.id}
                        onClick={() => { setTempProvinceId(p.id); setTempDistrictId(null); }}
                        className={cn(
                          "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-all",
                          tempProvinceId === p.id ? `${t.bgActive} ${t.textActive} font-semibold` : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        <span className="truncate">{p.name}</span>
                        <ChevronDown size={14} className="-rotate-90 text-gray-300" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Districts */}
                <div className="w-3/5 overflow-y-auto custom-scrollbar p-4">
                  {tempProvinceId ? (
                    <div className="grid grid-cols-1 gap-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">QUẬN / HUYỆN</p>
                      {districts.map((d: any) => (
                        <label
                          key={d.id}
                          className={cn(
                            "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer",
                            tempDistrictId === d.id ? `${t.bgActive} ${t.textActive} font-semibold` : "text-gray-600 hover:bg-gray-50"
                          )}
                        >
                          <span>{d.name}</span>
                          <div className={cn(
                            "w-4 h-4 rounded border flex items-center justify-center",
                            tempDistrictId === d.id ? `${t.bgButton} ${t.borderActive}` : "border-gray-300"
                          )}>
                            {tempDistrictId === d.id && <Check size={12} className="text-white" />}
                          </div>
                          <input
                            type="radio"
                            name="district"
                            className="hidden"
                            checked={tempDistrictId === d.id}
                            onChange={() => setTempDistrictId(d.id)}
                          />
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-3">
                      <MapPin size={40} className="opacity-20" />
                      <p className="text-sm">Vui lòng chọn Tỉnh/Thành phố</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={applyLocation} className={cn("px-8 h-10 rounded-xl text-sm font-bold text-white transition-all shadow-md", t.bgButton, t.bgButtonHover, t.shadowButton)}>Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Rent/Budget Range Selector */}
        <div className="relative flex-1 group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'rent' ? null : 'rent')}
            className={cn(
              "flex items-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors text-left",
              "hover:bg-gray-50",
              activeDropdown === 'rent' && "bg-gray-50"
            )}
          >
            <div className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center text-yellow-600 shrink-0">
              <Banknote size={18} />
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-gray-700 truncate">
                {priceMin === null && priceMax === null ? (type === 'property' ? "Giá thuê" : "Ngân sách") :
                  priceMin !== null && priceMax !== null ? `${priceMin / 1000000} - ${priceMax / 1000000} tr` :
                    priceMin !== null ? `Trên ${priceMin / 1000000} tr` :
                      priceMax !== null ? `Dưới ${priceMax / 1000000} tr` : (type === 'property' ? "Giá thuê" : "Ngân sách")}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {(priceMin !== null || priceMax !== null) && (
                <div
                  onClick={(e) => { e.stopPropagation(); setPriceMin(null); setPriceMax(null); handleSearch(); }}
                  className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center text-gray-400 transition-colors"
                >
                  <X size={12} />
                </div>
              )}
              <ChevronDown size={16} className={cn("text-gray-400 transition-transform", activeDropdown === 'rent' && "rotate-180")} />
            </div>
          </button>

          {activeDropdown === 'rent' && (
            <div className="absolute top-full left-0 mt-3 w-full md:w-[450px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">{type === 'property' ? "Khoảng giá thuê / tháng" : "Khoảng ngân sách / tháng"}</h3>
                <button onClick={() => { setPriceMin(null); setPriceMax(null); }} className={cn("text-xs font-medium hover:underline", t.textIcon)}>Xóa chọn</button>
              </div>

              <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-5">
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {RENT_RANGES.map((range, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setPriceMin(range.min);
                        setPriceMax(range.max);
                      }}
                      className={cn(
                        "flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all",
                        (priceMin === range.min && priceMax === range.max)
                          ? `${t.borderActive} ${t.bgActive} ${t.textActive} font-semibold`
                          : `border-gray-100 ${t.hoverBorder} hover:bg-gray-50 text-gray-600`
                      )}
                    >
                      <div className={cn(
                        "w-4 h-4 rounded-full border flex items-center justify-center transition-all",
                        (priceMin === range.min && priceMax === range.max) ? t.borderBtn : "border-gray-300"
                      )} />
                      <span className="text-sm">{range.label}</span>
                    </button>
                  ))}
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Hoặc nhập giá (VNĐ)</p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                      <NumberInput
                        placeholder="Từ"
                        value={priceMin || ''}
                        onChange={(e) => setPriceMin(e.target.value ? Number(e.target.value) : null)}
                        className={cn("w-full h-10 px-3 rounded-xl border border-gray-200 focus:ring-4 focus:shadow-md outline-none text-sm", t.focusBorder, t.focusRing)}
                      />
                    </div>
                    <span className="text-gray-400">-</span>
                    <div className="flex-1 relative">
                      <NumberInput
                        placeholder="Đến"
                        value={priceMax || ''}
                        onChange={(e) => setPriceMax(e.target.value ? Number(e.target.value) : null)}
                        className={cn("w-full h-10 px-3 rounded-xl border border-gray-200 focus:ring-4 focus:shadow-md outline-none text-sm", t.focusBorder, t.focusRing)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className={cn("px-8 h-10 rounded-xl text-sm font-bold text-white transition-all shadow-md", t.bgButton, t.bgButtonHover, t.shadowButton)}>Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        <div className="hidden md:block w-px h-8 self-center bg-gray-100 mx-1" />

        {/* Advanced Filters Trigger */}
        <div className="relative flex-[0.5] group">
          <button
            onClick={() => setActiveDropdown(activeDropdown === 'advanced' ? null : 'advanced')}
            className={cn(
              "flex items-center justify-center w-full h-12 px-2 gap-1.5 rounded-xl transition-colors",
              "hover:bg-gray-50",
              activeDropdown === 'advanced' && "bg-gray-50"
            )}
            title="Lọc nâng cao"
          >
            <SlidersHorizontal size={18} className="text-gray-500" />
            <ChevronDown size={14} className={cn("text-gray-400 transition-transform hidden lg:block", activeDropdown === 'advanced' && "rotate-180")} />
          </button>

          {activeDropdown === 'advanced' && (
            <div className="absolute top-full right-0 mt-3 w-full md:w-[600px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                <h3 className="font-bold text-gray-800">Lọc nâng cao</h3>
                <button onClick={() => {
                  setPropertyType(null);
                  setSeatingCapacityMin(null);
                  setSeatingCapacityMax(null);
                  setMinArea(null);
                  setMaxArea(null);
                  setHasParking(false);
                  setHasOutdoorSeating(false);
                  setIsAvailable(false);
                }} className={cn("text-xs font-medium hover:underline", t.textIcon)}>Xóa chọn</button>
              </div>

              {isAuthenticated ? (
                <div className="p-5 grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Loại hình mặt bằng</label>
                    <select
                      value={propertyType || ''}
                      onChange={(e) => setPropertyType(e.target.value || null)}
                      className={cn("w-full h-10 px-3 rounded-xl border border-gray-200 focus:ring-4 focus:shadow-md outline-none text-sm bg-white", t.focusBorder, t.focusRing)}
                    >
                      <option value="">Tất cả</option>
                      {PROPERTY_TYPES.map(pt => (
                        <option key={pt.value} value={pt.value}>{pt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Diện tích từ (m²)</label>
                    <NumberInput
                      placeholder="VD: 100"
                      value={minArea || ''}
                      onChange={(e) => setMinArea(e.target.value ? Number(e.target.value) : null)}
                      className={cn("w-full h-10 px-3 rounded-xl border border-gray-200 focus:ring-4 focus:shadow-md outline-none text-sm", t.focusBorder, t.focusRing)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Đến (m²)</label>
                    <NumberInput
                      placeholder="VD: 300"
                      value={maxArea || ''}
                      onChange={(e) => setMaxArea(e.target.value ? Number(e.target.value) : null)}
                      className={cn("w-full h-10 px-3 rounded-xl border border-gray-200 focus:ring-4 focus:shadow-md outline-none text-sm", t.focusBorder, t.focusRing)}
                    />
                  </div>

                  {type === 'property' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Sức chứa từ (người)</label>
                        <NumberInput
                          placeholder="VD: 50"
                          value={seatingCapacityMin || ''}
                          onChange={(e) => setSeatingCapacityMin(e.target.value ? Number(e.target.value) : null)}
                          className={cn("w-full h-10 px-3 rounded-xl border border-gray-200 focus:ring-4 focus:shadow-md outline-none text-sm", t.focusBorder, t.focusRing)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Đến (người)</label>
                        <NumberInput
                          placeholder="VD: 200"
                          value={seatingCapacityMax || ''}
                          onChange={(e) => setSeatingCapacityMax(e.target.value ? Number(e.target.value) : null)}
                          className={cn("w-full h-10 px-3 rounded-xl border border-gray-200 focus:ring-4 focus:shadow-md outline-none text-sm", t.focusBorder, t.focusRing)}
                        />
                      </div>
                      <div className="col-span-2 grid grid-cols-2 gap-3 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={hasParking}
                            onChange={(e) => setHasParking(e.target.checked)}
                            className="w-4 h-4 rounded accent-orange-500"
                          />
                          Có bãi đỗ xe
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={hasOutdoorSeating}
                            onChange={(e) => setHasOutdoorSeating(e.target.checked)}
                            className="w-4 h-4 rounded accent-orange-500"
                          />
                          Có không gian ngoài trời
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={isAvailable}
                            onChange={(e) => setIsAvailable(e.target.checked)}
                            className={cn("w-4 h-4 rounded", type === 'property' ? 'accent-orange-500' : 'accent-blue-500')}
                          />
                          Đang còn trống
                        </label>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="p-5 flex flex-col items-center justify-center text-center py-8">
                  🏢
                  <h4 className="font-bold text-gray-800 mt-2">Đăng nhập để mở khóa lọc nâng cao</h4>
                  <p className="text-xs text-gray-500 mt-1 mb-4 max-w-sm">Mở khóa tính năng lọc chi tiết theo diện tích, sức chứa, và tiện ích.</p>
                  <Link to="/login" className={cn("px-6 h-9 rounded-xl text-white font-bold text-xs flex items-center justify-center transition-colors shadow-md", t.bgButton, t.bgButtonHover, t.shadowButton)}>Đăng nhập ngay</Link>
                </div>
              )}

              <div className="p-4 bg-gray-50 border-t flex items-center justify-end gap-2">
                <button onClick={() => setActiveDropdown(null)} className="px-6 h-10 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-white transition-colors">Hủy</button>
                <button onClick={handleSearch} className={cn("px-8 h-10 rounded-xl text-sm font-bold text-white transition-all shadow-md", t.bgButton, t.bgButtonHover, t.shadowButton)}>Áp dụng</button>
              </div>
            </div>
          )}
        </div>

        {/* Search and Reset Buttons */}
        <div className="p-1 flex gap-2 shrink-0">
          <button
            onClick={resetAll}
            className={cn(
              "hidden lg:flex items-center justify-center gap-1 h-10 md:h-full px-4 rounded-xl border",
              "text-gray-500 text-sm font-medium hover:bg-gray-50 transition-colors"
            )}
            title="Xóa lọc"
          >
            <X size={16} />
          </button>
          <button
            onClick={handleSearch}
            className={cn(
              "flex-1 md:flex-none flex items-center justify-center gap-2 h-10 md:h-full px-8 rounded-xl",
              "text-white font-bold text-sm shadow-lg",
              "hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200",
              t.bgButton, t.bgButtonHover, t.shadowButton
            )}
          >
            <Search size={18} />
            <span className="md:hidden lg:inline">Tìm kiếm</span>
          </button>
        </div>

      </div>
    </div>
  );
};
